import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, Loader2, Check, Pencil, BookUser } from 'lucide-react';
import { expensesAPI } from '@/services/api';
import { isContactPickerSupported, pickContacts } from '@/utils/contactPicker';

const SplitNewPage = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [friendPhone, setFriendPhone] = useState('');
  const [friendName, setFriendName] = useState('');
  const [description, setDescription] = useState('');
  const [splitType, setSplitType] = useState('equal');
  const [customAmounts, setCustomAmounts] = useState({ payer: '', friend: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditingFriend, setIsEditingFriend] = useState(true);

  const totalAmount = parseFloat(amount) || 0;
  const isValidAmount = totalAmount > 0;
  const isValidPhone = friendPhone.length === 10;
  
  const splitAmount = splitType === 'equal' 
    ? totalAmount / 2 
    : parseFloat(customAmounts.friend) || 0;

  const isCustomValid = splitType === 'custom' 
    ? (parseFloat(customAmounts.payer) || 0) + (parseFloat(customAmounts.friend) || 0) === totalAmount
    : true;

  const canSubmit = isValidAmount && isValidPhone && isCustomValid;

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    // Only allow one decimal point
    const parts = value.split('.');
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    setAmount(value);
    setError('');

    // Reset custom amounts when total changes
    if (splitType === 'custom') {
      setCustomAmounts({ payer: '', friend: '' });
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFriendPhone(value);
    setError('');
  };

  const handleCustomAmountChange = (field, value) => {
    const numValue = value.replace(/[^\d.]/g, '');
    const newAmounts = { ...customAmounts, [field]: numValue };
    
    // Auto-calculate the other field
    if (field === 'friend' && numValue !== '') {
      const friendAmt = parseFloat(numValue) || 0;
      newAmounts.payer = (totalAmount - friendAmt).toFixed(2);
    } else if (field === 'payer' && numValue !== '') {
      const payerAmt = parseFloat(numValue) || 0;
      newAmounts.friend = (totalAmount - payerAmt).toFixed(2);
    }
    
    setCustomAmounts(newAmounts);
  };

  const clearFriend = () => {
    setFriendPhone('');
    setFriendName('');
    setIsEditingFriend(true);
  };

  const handleFriendDone = () => {
    if (isValidPhone) {
      setIsEditingFriend(false);
    }
  };

  const handlePickContact = async () => {
    try {
      const contacts = await pickContacts(false);
      if (contacts.length > 0) {
        const contact = contacts[0];
        if (contact.name) setFriendName(contact.name);
        if (contact.phone) {
          setFriendPhone(contact.phone);
          setError('');
        }
      }
    } catch (err) {
      console.error('Contact picker:', err);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setLoading(true);
    setError('');

    try {
      const data = {
        total_amount: totalAmount,
        friend_phone: friendPhone,
        description: description || undefined,
        split_type: splitType,
      };

      if (splitType === 'custom') {
        data.custom_amounts = {
          payer: parseFloat(customAmounts.payer),
          friend: parseFloat(customAmounts.friend),
        };
      }

      const response = await expensesAPI.create(data);
      navigate(`/expenses/${response.data.id}/request`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to create split';
      setError(typeof message === 'string' ? message : 'Failed to create split');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    if (!num || isNaN(num)) return '₹0';
    return `₹${parseFloat(num).toLocaleString('en-IN')}`;
  };

  // Show the friend card only if we have a valid phone AND not in editing mode
  const showFriendCard = isValidPhone && !isEditingFriend;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-6 py-4 flex items-center gap-4 border-b-2 border-black">
        <button 
          onClick={() => navigate('/home')}
          className="hover:opacity-70 transition-opacity"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-xl font-heading font-bold">Quick Split</h1>
      </header>

      <main className="flex-1 p-6 pb-32 space-y-6">
        {/* Amount Input */}
        <div className="text-center">
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide">
            Total Amount
          </label>
          <div className="relative mt-2">
            <span className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-[calc(50%+2ch)] text-5xl font-heading font-bold text-neutral-300">
              ₹
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0"
              className="input-amount w-full"
              autoFocus
              data-testid="amount-input"
            />
          </div>
        </div>

        {/* Friend Selection */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Split with
          </label>
          
          {showFriendCard ? (
            <div 
              className="card-base flex items-center gap-4 cursor-pointer hover:bg-neutral-50 transition-colors"
              onClick={() => setIsEditingFriend(true)}
              data-testid="friend-card"
            >
              <div className="w-12 h-12 rounded-full bg-brand-purple text-white flex items-center justify-center font-bold">
                {friendName ? friendName[0].toUpperCase() : friendPhone[0]}
              </div>
              <div className="flex-1">
                <p className="font-bold">{friendName || 'Friend'}</p>
                <p className="text-sm text-neutral-500">+91 {friendPhone}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingFriend(true);
                }}
                className="w-8 h-8 rounded-full hover:bg-neutral-200 flex items-center justify-center text-neutral-500"
                data-testid="edit-friend-button"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearFriend();
                }}
                className="w-8 h-8 rounded-full hover:bg-red-100 flex items-center justify-center text-red-500"
                data-testid="clear-friend-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex items-center justify-center w-16 h-14 rounded-xl border-2 border-black bg-neutral-100 font-bold text-sm flex-shrink-0">
                  +91
                </div>
                <input
                  type="tel"
                  value={friendPhone}
                  onChange={handlePhoneChange}
                  placeholder="Friend's phone number"
                  className="input-base flex-1 min-w-0"
                  data-testid="friend-phone-input"
                  autoFocus={!isValidAmount}
                />
                {isContactPickerSupported() && (
                  <button
                    type="button"
                    onClick={handlePickContact}
                    className="w-14 h-14 rounded-xl border-2 border-violet-300 bg-violet-50 text-violet-600 flex items-center justify-center hover:bg-violet-100 hover:border-violet-500 transition-all flex-shrink-0"
                    data-testid="pick-contact-button"
                    title="Pick from contacts"
                  >
                    <BookUser className="w-5 h-5" />
                  </button>
                )}
                {isValidPhone && (
                  <button
                    onClick={handleFriendDone}
                    className="w-14 h-14 rounded-xl border-2 border-black bg-emerald-500 text-white flex items-center justify-center flex-shrink-0"
                    data-testid="confirm-friend-button"
                  >
                    <Check className="w-6 h-6" />
                  </button>
                )}
              </div>
              <input
                type="text"
                value={friendName}
                onChange={(e) => setFriendName(e.target.value)}
                placeholder="Friend's name (optional)"
                className="input-base"
                data-testid="friend-name-input"
              />
            </div>
          )}
        </div>

        {/* Split Type */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Split Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setSplitType('equal')}
              className={`py-4 px-4 rounded-xl border-2 font-bold transition-all ${
                splitType === 'equal'
                  ? 'border-black bg-black text-white'
                  : 'border-black bg-white text-black hover:bg-neutral-100'
              }`}
              data-testid="split-type-equal"
            >
              Equal (50/50)
            </button>
            <button
              onClick={() => setSplitType('custom')}
              className={`py-4 px-4 rounded-xl border-2 font-bold transition-all ${
                splitType === 'custom'
                  ? 'border-black bg-black text-white'
                  : 'border-black bg-white text-black hover:bg-neutral-100'
              }`}
              data-testid="split-type-custom"
            >
              Custom
            </button>
          </div>
        </div>

        {/* Split Preview / Custom Inputs */}
        <div className="card-base">
          {splitType === 'equal' ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">You paid</span>
                <span className="font-bold text-lg">{formatCurrency(totalAmount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">They owe</span>
                <span className="font-bold text-lg text-money-out">{formatCurrency(splitAmount)}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-neutral-600 w-24">You paid</span>
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={customAmounts.payer}
                    onChange={(e) => handleCustomAmountChange('payer', e.target.value)}
                    placeholder="0"
                    className="input-base pl-8"
                    data-testid="custom-payer-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-neutral-600 w-24">They owe</span>
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">₹</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={customAmounts.friend}
                    onChange={(e) => handleCustomAmountChange('friend', e.target.value)}
                    placeholder="0"
                    className="input-base pl-8"
                    data-testid="custom-friend-input"
                  />
                </div>
              </div>
              <div className={`flex items-center justify-between pt-3 border-t-2 border-dashed ${
                isCustomValid ? 'border-green-300' : 'border-red-300'
              }`}>
                <span className="text-neutral-600">Total</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatCurrency(totalAmount)}</span>
                  {isCustomValid ? (
                    <Check className="w-5 h-5 text-success" />
                  ) : (
                    <span className="text-error text-sm">Doesn't match</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="text-sm font-medium text-neutral-500 uppercase tracking-wide block mb-2">
            Description (optional)
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Dinner, Uber, etc."
            className="input-base"
            data-testid="description-input"
          />
        </div>

        {error && (
          <p className="text-error text-sm font-medium text-center" data-testid="error-message">
            {error}
          </p>
        )}
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 bg-white border-t-2 border-black">
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-split-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              Create Split
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SplitNewPage;
